import React, { useState } from 'react'
import { Box, Card, CardContent } from '@mui/material'
import dayjs, { type Dayjs } from 'dayjs'

import { FormControlCheckbox, PhoneEntryBox } from '~components/common'
import MultiDatePicker from '~components/common/MultiDatePicker'
import { Icon, IconButton, InputLabelStyled, TextField, Typography } from '~components/internal'
import { type EnrichedAIPhoneLineConfig, phoneWithExtension } from '~types'

import GreetingField from './GreetingField'
import { transferLineFields } from './utils'

interface AIPhoneLineHolidayCardProps {
  config: EnrichedAIPhoneLineConfig
  index: number
  onUpdate: (index: number, config: EnrichedAIPhoneLineConfig) => void
  onDelete: (index: number) => void
}

const AIPhoneLineHolidayCard: React.FC<AIPhoneLineHolidayCardProps> = ({
  config,
  index,
  onUpdate,
  onDelete,
}) => {
  const [transferNumber, setTransferNumber] = useState<string>(
    phoneWithExtension(config.default_transfer_number, config.default_transfer_extension),
  )
  const [_isValidTransferPhone, setIsValidTransferPhone] = useState<boolean>(
    !!config.default_transfer_number,
  )

  const handleTransferPhoneChange = (newPhoneString: string, newPhoneIsValid: boolean): void => {
    setTransferNumber(newPhoneString)
    setIsValidTransferPhone(newPhoneIsValid)
    onUpdate(index, { ...config, ...transferLineFields(newPhoneString) })
  }

  const handleDaysSelected = (days: Dayjs[]): void => {
    onUpdate(index, { ...config, inactive_dates: days.map((d) => d.format('YYYY-MM-DD')) })
  }

  return (
    <Card className='phone-line-config-card'>
      <CardContent>
        <Box className='phone-line-config-card__header'>
          <Box className='phone-line-config-card__title'>
            <Icon name='dateRange' />
            <Typography variant='h6'>{config.name || `Holiday ${index + 1}`}</Typography>
          </Box>
          <IconButton size='small' color='error' onClick={() => onDelete(index)}>
            <Icon name='delete' />
          </IconButton>
        </Box>

        <Box className='phone-line-config-card__content'>
          <TextField
            label='Holiday Name'
            value={config.name}
            onChange={(e) => onUpdate(index, { ...config, name: e.target.value })}
          />

          <GreetingField
            label='Greeting Message'
            value={config.greeting_message ?? ''}
            onChange={(greeting) => onUpdate(index, { ...config, greeting_message: greeting })}
            placeholder='Enter the greeting message for this time period...'
          />

          <Box className='phone-line-config-card__date-row'>
            <MultiDatePicker
              selectedDays={config.inactive_dates?.map((date: string) => dayjs(date)) || []}
              setSelectedDays={handleDaysSelected}
              boxShadow
            />
          </Box>

          <Box>
            <InputLabelStyled shrink className='phone-line-dialog__label'>
              Default Transfer Line
            </InputLabelStyled>
            <PhoneEntryBox inputPhoneState={transferNumber} onChange={handleTransferPhoneChange} />
          </Box>

          <FormControlCheckbox
            label='Apply to all transfer lines'
            checked={config.applied_to_transfer_lines || false}
            onChange={() =>
              onUpdate(index, {
                ...config,
                applied_to_transfer_lines: !config.applied_to_transfer_lines,
              })
            }
          />
        </Box>
      </CardContent>
    </Card>
  )
}

export default AIPhoneLineHolidayCard
